import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api, { getAccessToken, setAccessToken } from '@/lib/api';
import { authService } from '@/services/auth.service';

const AuthContext = createContext(null);

/**
 * Auth session for the app shell.
 * Access token in memory/localStorage; refresh via HttpOnly cookie (Axios credentials).
 */
export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => getAccessToken());
  const [user, setUser] = useState(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function ensureAccessToken() {
      const existing = getAccessToken();
      if (existing) return existing;

      // Cookie-only recovery: access token missing but refresh cookie may still be valid.
      try {
        const { data } = await api.post('/auth/refresh', {});
        const next = data?.data?.accessToken;
        if (next) {
          setAccessToken(next, { broadcast: false });
          return next;
        }
      } catch {
        return null;
      }
      return null;
    }

    async function bootstrap() {
      try {
        const existing = await ensureAccessToken();
        if (!existing) {
          if (!cancelled) {
            setToken(null);
            setUser(null);
          }
          return;
        }

        const me = await authService.me();
        if (cancelled) return;
        setToken(existing);
        setUser(me);
      } catch {
        if (cancelled) return;
        setAccessToken(null, { broadcast: false });
        setToken(null);
        setUser(null);
      } finally {
        if (!cancelled) setIsBootstrapping(false);
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep React token state in sync with interceptor / other-tab refresh.
  useEffect(() => {
    function onToken(event) {
      const next = event.detail?.token ?? null;
      setToken(next);
      if (!next) setUser(null);
    }

    window.addEventListener('auth:token', onToken);
    return () => window.removeEventListener('auth:token', onToken);
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      isBootstrapping,
      isAuthenticated: Boolean(token),
      setSession({ accessToken, user: nextUser }) {
        setAccessToken(accessToken);
        setToken(accessToken);
        setUser(nextUser ?? null);
      },
      clearSession() {
        setAccessToken(null);
        setToken(null);
        setUser(null);
      },
      async refreshUser() {
        const me = await authService.me();
        setUser(me);
        return me;
      },
      setUser,
    }),
    [token, user, isBootstrapping],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
